import { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import {
  Activity,
  ArrowLeft,
  Bot,
  CreditCard,
  FileText,
  Gift,
  LayoutDashboard,
  Menu,
  Moon,
  Radio,
  ScrollText,
  ShieldCheck,
  Sun,
  Users,
  X,
} from "lucide-react";
import { T, useAdmin, useOpsTheme } from "@/components/admin/ui";
import { adminUrl } from "@/lib/adminPath";

const NAV = [
  { to: "", label: "Overview", icon: LayoutDashboard, testid: "admin-nav-overview", perm: "users.view" },
  { to: "/live", label: "Live view", icon: Radio, testid: "admin-nav-live", perm: "users.view", live: true },
  { to: "/monitoring", label: "API monitor", icon: Activity, testid: "admin-nav-monitoring", perm: "metrics.view" },
  { to: "/users", label: "Customers", icon: Users, testid: "admin-nav-users", perm: "users.view" },
  { to: "/proposals", label: "Proposals", icon: FileText, testid: "admin-nav-proposals", perm: "proposals.view" },
  { to: "/payments", label: "Payments", icon: CreditCard, testid: "admin-nav-payments", perm: "payments.view" },
  { to: "/appsumo", label: "AppSumo codes", icon: Gift, testid: "admin-nav-appsumo", perm: "ai.manage" },
  { to: "/ai", label: "AI providers", icon: Bot, testid: "admin-nav-ai", perm: "ai.manage" },
  { to: "/audit", label: "Audit log", icon: ScrollText, testid: "admin-nav-audit", perm: "audit.view" },
];

const ROLE_TONE = {
  super_admin: "bg-emerald-50 text-emerald-700 ring-emerald-600/20 dark:bg-emerald-500/10 dark:text-emerald-400",
  admin: "bg-blue-50 text-blue-700 ring-blue-600/20 dark:bg-blue-500/10 dark:text-blue-300",
  support: "bg-slate-100 text-slate-600 ring-slate-500/20 dark:bg-slate-800 dark:text-slate-300",
};

export const OpsShell = ({ children, title, subtitle, actions }) => {
  const { me, can } = useAdmin();
  const { dark, toggle } = useOpsTheme();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const nav = NAV.filter((n) => can(n.perm));

  const Rail = () => (
    <>
      <div className="px-4 py-4 border-b border-slate-200 dark:border-slate-800">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-lg bg-emerald-600 dark:bg-emerald-500 grid place-items-center shadow-sm">
            <ShieldCheck className="w-4 h-4 text-white dark:text-slate-950" />
          </div>
          <div className="leading-tight min-w-0">
            <p className="text-sm font-semibold tracking-tight text-slate-900 dark:text-slate-50">Propojo</p>
            <p className="text-[10px] uppercase tracking-[0.14em] text-emerald-600 dark:text-emerald-400 font-medium">
              Ops console
            </p>
          </div>
        </div>
      </div>

      <nav className="flex-1 p-2 space-y-0.5 overflow-y-auto">
        {nav.map(({ to, label, icon: Icon, testid, live }) => (
          <NavLink
            key={to}
            to={adminUrl(to)}
            end={to === ""}
            data-testid={testid}
            onClick={() => setOpen(false)}
            className={({ isActive }) =>
              `flex items-center gap-2.5 rounded-md px-2.5 py-2 text-[13px] font-medium transition-colors ${
                isActive
                  ? "bg-emerald-50 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-400"
                  : "text-slate-600 hover:bg-slate-100 hover:text-slate-900 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-slate-100"
              }`
            }
          >
            <Icon className="w-4 h-4 shrink-0" />
            <span className="truncate">{label}</span>
            {live && (
              <span className="ml-auto relative flex w-1.5 h-1.5">
                <span className="absolute inline-flex w-full h-full rounded-full bg-emerald-500 opacity-75 animate-ping" />
                <span className="relative inline-flex w-1.5 h-1.5 rounded-full bg-emerald-500" />
              </span>
            )}
          </NavLink>
        ))}
      </nav>

      <div className="p-3 border-t border-slate-200 dark:border-slate-800 space-y-3">
        <div className="min-w-0">
          <p className="text-[13px] font-medium text-slate-800 dark:text-slate-200 truncate">{me?.name || "Staff"}</p>
          <p className={`text-[11px] ${T.muted} truncate`}>{me?.email}</p>
          <span
            className={`inline-block mt-1.5 rounded-full px-2 py-0.5 text-[10px] font-medium uppercase tracking-[0.08em] ring-1 ring-inset ${
              ROLE_TONE[me?.platform_role] || ROLE_TONE.support
            }`}
            data-testid="admin-role-badge"
          >
            {me?.platform_role?.replace("_", " ")}
          </span>
        </div>
        <div className="flex gap-2">
          <button
            data-testid="admin-theme-toggle"
            onClick={toggle}
            title={dark ? "Switch to light" : "Switch to dark"}
            className={`${T.btn} ${T.btnGhost} px-2`}
          >
            {dark ? <Sun className="w-3.5 h-3.5" /> : <Moon className="w-3.5 h-3.5" />}
          </button>
          <button data-testid="admin-back-to-app" onClick={() => navigate("/dashboard")} className={`${T.btn} ${T.btnGhost} flex-1`}>
            <ArrowLeft className="w-3.5 h-3.5" /> App
          </button>
        </div>
      </div>
    </>
  );

  return (
    <div className={`${T.page} flex`}>
      <aside className="hidden lg:flex w-[216px] shrink-0 flex-col border-r border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 sticky top-0 h-screen">
        <Rail />
      </aside>

      {open && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div className="absolute inset-0 bg-slate-900/50 backdrop-blur-sm" onClick={() => setOpen(false)} />
          <aside className="relative w-[240px] flex flex-col bg-white dark:bg-slate-900 border-r border-slate-200 dark:border-slate-800">
            <button
              data-testid="admin-mobile-nav-close"
              onClick={() => setOpen(false)}
              className="absolute right-2 top-3.5 p-1 text-slate-400 hover:text-slate-700 dark:hover:text-slate-200"
            >
              <X className="w-4 h-4" />
            </button>
            <Rail />
          </aside>
        </div>
      )}

      <main className="flex-1 min-w-0">
        <header className="sticky top-0 z-30 bg-white/95 dark:bg-slate-900/95 backdrop-blur border-b border-slate-200 dark:border-slate-800">
          <div className="max-w-[1600px] mx-auto px-4 sm:px-6 py-3.5 flex items-center justify-between gap-4">
            <div className="flex items-center gap-3 min-w-0">
              <button
                data-testid="admin-mobile-nav-open"
                onClick={() => setOpen(true)}
                className="lg:hidden p-1.5 -ml-1 rounded-md text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800"
              >
                <Menu className="w-4.5 h-4.5" />
              </button>
              <div className="min-w-0">
                <h1 className={`${T.h1} truncate`}>{title}</h1>
                {subtitle && <p className={`text-xs sm:text-[13px] mt-0.5 ${T.muted} truncate`}>{subtitle}</p>}
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">{actions}</div>
          </div>
        </header>
        <div className="max-w-[1600px] mx-auto p-4 sm:p-6 space-y-5">{children}</div>
      </main>
    </div>
  );
};
